/* eslint-disable max-lines */
// Refactored from https://github.com/Azure-Samples/ms-identity-javascript-react-tutorial/blob/main/1-Authentication/1-sign-in/SPA/src/authConfig.js

import type { AuthCredentialRefreshResult } from "./auth-refresh-coordinator"
import type {
  AccountInfo,
  AuthenticationResult,
  IPublicClientApplication,
} from "@azure/msal-browser"

import { currentPathWithQueryHash } from "@/features/auth/utils/auth-constants"
import { storeAuthError } from "@/features/auth/utils/auth-error-store"
import { guardAuthLoop } from "@/features/auth/utils/auth-loop-guard"
import { isPopulatedMsalAuth } from "@/features/auth/utils/msal-config-validator"
import { logger } from "@/lib/logger"

import {
  captureAuthSnapshot,
  configureAuthRecovery,
  hasRegisteredAuthSession,
  invalidateAuthSession,
  isAuthSnapshotCurrent,
  recoverFromUnauthorized,
  refreshCredentialsProactively,
  registerAuthSession,
  requestTerminalRecovery,
} from "./auth-refresh-coordinator"
import { BACKEND_URI } from "../constants/config"

// Authentication mode enum
export enum AuthMode {
  UNKNOWN = "unknown",
  EASYAUTH = "easyauth",
  MSAL = "msal",
}

// Current authentication mode
let currentAuthMode = AuthMode.UNKNOWN
let easyAuthSupported: boolean | null = null

export type AuthSetupStatus = "ok" | "backend-unavailable"

interface AuthSetup {
  // Set to true if login elements should be shown in the UI
  useLogin: boolean
  // Indicates whether the auth setup was successfully loaded from the backend.
  // "backend-unavailable" means the backend never returned a valid MSAL config (e.g. still warming up
  // in dev, or unreachable). Consumers should NOT initialize MSAL when this is set.
  status: AuthSetupStatus
  /**
   * Configuration object to be passed to MSAL instance on creation.
   * For a full list of MSAL.js configuration parameters, visit:
   * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/configuration.md
   */
  msalConfig: {
    auth: {
      clientId: string // Client app id used for login
      authority: string // Directory to use for login https://learn.microsoft.com/azure/active-directory/develop/msal-client-application-configuration#authority
      redirectUri: string // Points to window.location.origin. You must register this URI on Azure Portal/App Registration.
      postLogoutRedirectUri: string // Indicates the page to navigate after logout.
      navigateToLoginRequestUrl: boolean // If "true", will navigate back to the original request location before processing the auth code response.
    }
    cache: {
      cacheLocation: string // Configures cache location. "sessionStorage" is more secure, but "localStorage" gives you SSO between tabs.
      storeAuthStateInCookie: boolean // Set this to "true" if you are having issues on IE11 or Edge
    }
  }
  loginRequest: {
    /**
     * Scopes you add here will be prompted for user consent during sign-in.
     * By default, MSAL.js will add OIDC scopes (openid, profile, email) to any login request.
     * For more information about OIDC scopes, visit:
     * https://docs.microsoft.com/azure/active-directory/develop/v2-permissions-and-consent#openid-connect-scopes
     */
    scopes: Array<string>
  }
  tokenRequest: {
    scopes: Array<string>
  }
}

function backendUnavailableSetup(): AuthSetup {
  return {
    useLogin: false,
    status: "backend-unavailable",
    msalConfig: {
      auth: {
        clientId: "",
        authority: "",
        redirectUri: "",
        postLogoutRedirectUri: "",
        navigateToLoginRequestUrl: false,
      },
      cache: {
        cacheLocation: "sessionStorage",
        storeAuthStateInCookie: false,
      },
    },
    loginRequest: { scopes: [] },
    tokenRequest: { scopes: [] },
  }
}

// Backoff schedule (ms) for polling the auth setup endpoint while the backend warms up.
// Total wait ≈ 17.5s before giving up and surfacing a clear error to the UI.
const AUTH_SETUP_RETRY_DELAYS_MS = [500, 1000, 1500, 2000, 2500, 3000, 3000, 4000]

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

// Fetch the auth setup JSON data from the API. Polls with backoff if the backend
// is not yet ready (HTTP 503 or network error) so the frontend doesn't initialize
// MSAL with an empty/placeholder client_id and trigger a redirect loop.
async function fetchAuthSetup(): Promise<AuthSetup> {
  const attempts = AUTH_SETUP_RETRY_DELAYS_MS.length + 1
  let lastError: unknown = null

  for (let attempt = 0; attempt < attempts; attempt++) {
    try {
      const response = await fetch(`${BACKEND_URI}/auth_setup`)
      if (response.status === 503) {
        // Backend explicitly says "still starting"; wait and try again.
        lastError = new Error("auth setup endpoint returned 503 (backend starting)")
      } else if (!response.ok) {
        throw new Error(`auth setup response was not ok: ${response.status}`)
      } else {
        const data = (await response.json()) as Omit<AuthSetup, "status"> & {
          status?: AuthSetupStatus
        }
        if (!isPopulatedMsalAuth(data.msalConfig.auth)) {
          // Defense in depth: backend forgot to gate this, but the config is still garbage.
          lastError = new Error("auth setup returned placeholder/empty MSAL config")
        } else {
          return { ...data, status: "ok" }
        }
      }
    } catch (error) {
      lastError = error
      logger.debug(`Auth setup fetch attempt ${attempt + 1} failed`, error as Error)
    }

    if (attempt < AUTH_SETUP_RETRY_DELAYS_MS.length) {
      await delay(AUTH_SETUP_RETRY_DELAYS_MS[attempt])
    }
  }

  logger.error("Error fetching auth setup after retries", lastError as Error)
  return backendUnavailableSetup()
}

// Check if EasyAuth is available by calling the auth/me endpoint
export async function checkEasyAuth(): Promise<boolean> {
  try {
    logger.debug("Checking EasyAuth availability...")

    // Use the root URL instead of BACKEND_URI which includes /api/
    const rootUrl = window.location.origin
    const meUrl = `${rootUrl}/.auth/me`

    const response = await fetch(meUrl)

    if (!response.ok) {
      logger.debug(`EasyAuth check failed, response status: ${response.status}`)
      return false
    }

    const data = await response.json()
    logger.debug("EasyAuth response received", data)

    // Check if the response contains user information
    // The response is an array where each item represents an authentication provider
    if (!Array.isArray(data) || data.length === 0 || !data[0].user_id) {
      logger.debug("EasyAuth response missing user information")
      return false
    }

    // Successfully authenticated
    // Provider selection should be stable; treat this as an authentication success signal.
    currentAuthMode = AuthMode.EASYAUTH
    easyAuthSupported = true

    const authInfo: EasyAuthInfo = data[0]

    logger.info("EasyAuth authentication successful", {
      userId: authInfo.user_id,
      provider: authInfo.provider_name,
    })

    // Store the full auth info for later use
    storeEasyAuthInfo(authInfo)

    // Check for token expiration
    checkAndHandleTokenExpiration(authInfo)

    return true
  } catch (error) {
    logger.error("Error checking EasyAuth availability", error as Error)
    return false
  }
}

/**
 * True if the current origin is a local dev host where EasyAuth (an Azure App Service
 * feature) is structurally unavailable. Used to avoid false-positives from the EasyAuth
 * probe heuristics when a Vite/local backend is restarting.
 */
export function isLocalDevHost(): boolean {
  try {
    const hostname = window.location.hostname
    return (
      hostname === "localhost" ||
      hostname === "127.0.0.1" ||
      hostname === "0.0.0.0" ||
      hostname === "[::1]" ||
      hostname.endsWith(".localhost")
    )
  } catch {
    return false
  }
}

/**
 * Probe whether EasyAuth is supported on this host.
 *
 * Important: This must NOT depend on whether the user is currently authenticated.
 * We use heuristics to avoid false-positives in local SPA dev servers that may
 * history-fallback unknown paths (returning HTML for `/.auth/me`).
 */
export async function probeEasyAuthSupport(): Promise<boolean> {
  if (easyAuthSupported !== null) return easyAuthSupported

  // EasyAuth is an Azure App Service feature. It cannot be present on a local dev host,
  // so don't even probe — a transient network error during a backend restart would
  // otherwise flip us into EasyAuth mode and cause /.auth/login/aad redirects that 404.
  if (isLocalDevHost()) {
    easyAuthSupported = false
    return false
  }

  try {
    const rootUrl = window.location.origin
    const meUrl = `${rootUrl}/.auth/me`
    const response = await fetch(meUrl, { credentials: "include" })

    if (response.status === 404) {
      easyAuthSupported = false
      return false
    }

    const contentType = response.headers.get("content-type") ?? ""

    // Local dev servers often return index.html (text/html) for unknown paths; treat as unsupported.
    if (contentType.includes("text/html")) {
      easyAuthSupported = false
      return false
    }

    // Any non-404 response indicates the endpoint exists (even if unauthenticated).
    if (!response.ok) {
      easyAuthSupported = true
      return true
    }

    // If OK, ensure it looks like the real EasyAuth JSON response.
    if (!contentType.includes("application/json")) {
      easyAuthSupported = false
      return false
    }

    const data = await response.json()
    const supported = Array.isArray(data)
    easyAuthSupported = supported
    return supported
  } catch (error) {
    // A network error can happen if `/.auth/me` triggers a redirect to an external IdP (CORS).
    // In that case, the endpoint still exists → treat EasyAuth as supported.
    logger.debug("EasyAuth probe hit a network error; assuming supported", error as Error)
    easyAuthSupported = true
    return true
  }
}

// Define EasyAuth types
interface EasyAuthClaim {
  typ: string
  val: string
}

interface EasyAuthInfo {
  user_id: string
  provider_name: string
  user_claims?: EasyAuthClaim[]
  expires_on?: string
  // Use Record<string, unknown> instead of any for additional properties
  [key: string]: unknown
}

// Store EasyAuth info in sessionStorage for later use
function storeEasyAuthInfo(authInfo: EasyAuthInfo): void {
  try {
    // Store only what we need to avoid storing sensitive information
    const essentialInfo = {
      user_id: authInfo.user_id,
      provider_name: authInfo.provider_name,
      user_claims: authInfo.user_claims,
      expires_on: authInfo.expires_on,
    }

    sessionStorage.setItem("easyauth_info", JSON.stringify(essentialInfo))
    logger.debug("EasyAuth info stored in session storage")
  } catch (error) {
    logger.error("Error storing EasyAuth info", error as Error)
  }
}

// Get stored EasyAuth info
export function getStoredEasyAuthInfo(): EasyAuthInfo | null {
  try {
    const storedInfo = sessionStorage.getItem("easyauth_info")
    if (!storedInfo) {
      return null
    }
    return JSON.parse(storedInfo) as EasyAuthInfo
  } catch (error) {
    logger.error("Error retrieving stored EasyAuth info", error as Error)
    return null
  }
}

// Helper function to check token expiration and refresh if needed
function checkAndHandleTokenExpiration(authInfo: EasyAuthInfo): void {
  if (!authInfo.expires_on) {
    logger.debug("EasyAuth token does not have an expires_on property")
    return
  }

  try {
    const now = new Date()

    const expiresOn = new Date(authInfo.expires_on)

    const timeUntilExpiry = expiresOn.getTime() - now.getTime()

    logger.debug("EasyAuth token expiration details", {
      expiresOn: expiresOn.toISOString(),
      timeUntilExpiryMs: timeUntilExpiry,
      expiresInMinutes: Math.floor(timeUntilExpiry / (60 * 1000)),
    })

    // If token is expired or about to expire (within 5 minutes)
    if (timeUntilExpiry < 5 * 60 * 1000) {
      logger.warn("EasyAuth token is expired or about to expire soon")

      // Boot may precede session registration. The underlying operation shares
      // its flight with the registered scheduler/reactive driver.
      const expected = easyAuthIdentityOf(authInfo)
      const snapshot = captureAuthSnapshot()
      const registered = hasRegisteredAuthSession()
      const isCurrent = () =>
        currentAuthMode === AuthMode.EASYAUTH &&
        expected !== null &&
        easyAuthIdentityOf(getStoredEasyAuthInfo()) === expected &&
        (!registered || isAuthSnapshotCurrent(snapshot))
      void refreshEasyAuthSession(isCurrent).then((result) => {
        if (result.status === "failed" && isCurrent()) {
          requestTerminalRecovery(registered ? snapshot : captureAuthSnapshot())
        }
      })
    }
  } catch (error) {
    logger.error("Error parsing token expiration date", error as Error)
  }
}

// Setup proactive token refresh to minimize redirect scenarios
export function setupTokenRefreshScheduler(): void {
  const intervalSeconds = 240

  // Check token status immediately
  void checkAndRefreshTokenIfNeeded()

  // Then check periodically
  setInterval(() => {
    void checkAndRefreshTokenIfNeeded()
  }, intervalSeconds * 1000)
}

async function checkAndRefreshTokenIfNeeded(): Promise<void> {
  if (currentAuthMode !== AuthMode.EASYAUTH) {
    return
  }

  const authInfo = getStoredEasyAuthInfo()
  if (!authInfo || !authInfo.expires_on) {
    return
  }

  const snapshot = captureAuthSnapshot()
  try {
    const now = new Date()
    const expiresOn = new Date(authInfo.expires_on)

    const timeUntilExpiry = expiresOn.getTime() - now.getTime()

    // Refresh if less than 5 minutes until expiry
    if (timeUntilExpiry < 5 * 60 * 1000) {
      logger.info("Proactively refreshing token before expiration")

      // Goes through the coordinator so a proactive refresh and a concurrent
      // 401 storm share one hit on the underlying refresh endpoint, and so a
      // failure here produces the same single, deduplicated re-login as a
      // failed 401 recovery instead of a second forced logout.
      const outcome = await refreshCredentialsProactively()

      if (outcome.status === "failed") {
        logger.error("Proactive token refresh failed - forcing logout", {
          reason: outcome.reason,
        })
      }
    }
  } catch (error) {
    // Same forced re-login as before, but deduplicated with the 401 path so it
    // cannot stack a second redirect on top of one already under way.
    logger.error("Error in proactive token refresh - forcing logout", error as Error)
    requestTerminalRecovery(snapshot)
  }
}

/** Provider + tenant + object binding, captured before refreshing cookies. */
function easyAuthIdentityOf(info: EasyAuthInfo | null | undefined): string | null {
  if (!info?.provider_name || !info.user_id) return null
  const claim = (...names: string[]) => info.user_claims?.find((c) => names.includes(c.typ))?.val
  const tenant = claim("tid", "http://schemas.microsoft.com/identity/claims/tenantid")
  const object =
    claim("oid", "http://schemas.microsoft.com/identity/claims/objectidentifier") ?? info.user_id
  if (!tenant || !object) return null
  return JSON.stringify([info.provider_name, tenant, object, info.user_id])
}

// Share the network operation, not publication. Each waiter must still prove
// ownership before publishing its result. This also covers pre-registration boot.
let easyAuthFlight: {
  identity: string
  isCurrent: () => boolean
  promise: Promise<EasyAuthInfo | null>
} | null = null

async function acquireEasyAuthInfo(): Promise<EasyAuthInfo | null> {
  try {
    const root = window.location.origin
    const response = await fetch(`${root}/.auth/refresh`)
    if (!response.ok) return null
    const me = await fetch(`${root}/.auth/me`)
    if (!me.ok) return null
    const data = await me.json()
    if (!Array.isArray(data) || !easyAuthIdentityOf(data[0])) return null
    const info: EasyAuthInfo = data[0]
    return info
  } catch (error) {
    logger.error("Error refreshing EasyAuth token", error as Error)
    return null
  }
}

async function refreshEasyAuthSession(guard?: () => boolean): Promise<AuthCredentialRefreshResult> {
  const expected = easyAuthIdentityOf(getStoredEasyAuthInfo())
  const snapshot = captureAuthSnapshot()
  const registered = hasRegisteredAuthSession()
  const isCurrent = () =>
    currentAuthMode === AuthMode.EASYAUTH &&
    expected !== null &&
    easyAuthIdentityOf(getStoredEasyAuthInfo()) === expected &&
    (!registered || isAuthSnapshotCurrent(snapshot)) &&
    (guard?.() ?? true)
  if (!expected) return { status: "failed", reason: "easyauth-identity-missing" }
  if (!isCurrent()) return { status: "session-changed" }
  if (!easyAuthFlight || easyAuthFlight.identity !== expected || !easyAuthFlight.isCurrent()) {
    const flight = { identity: expected, isCurrent, promise: acquireEasyAuthInfo() }
    easyAuthFlight = flight
    void flight.promise.finally(() => {
      if (easyAuthFlight === flight) easyAuthFlight = null
    })
  }
  const info = await easyAuthFlight.promise
  if (!isCurrent()) return { status: "session-changed" }
  if (!info) return { status: "failed", reason: "easyauth-refresh-rejected" }
  if (easyAuthIdentityOf(info) !== expected) {
    // Another tab changed the shared cookie. Keep B out of A's storage and
    // invalidate A before any waiter can turn its failure into a logout of B.
    invalidateAuthSession(registered ? snapshot : captureAuthSnapshot())
    return { status: "session-changed" }
  }
  if (info.expires_on && !(new Date(info.expires_on).getTime() - Date.now() > 5 * 60 * 1000)) {
    return { status: "failed", reason: "easyauth-refresh-rejected" }
  }
  storeEasyAuthInfo(info)
  return { status: "refreshed" }
}

export async function refreshEasyAuthToken(guard?: () => boolean): Promise<boolean> {
  return (await refreshEasyAuthSession(guard)).status === "refreshed"
}

// Force logout - clears session and forces fresh login without account picker
export async function forceLogout(isCurrent: () => boolean = () => true): Promise<void> {
  if (!isCurrent()) return
  const mode = currentAuthMode
  const live = () => isCurrent() && currentAuthMode === mode
  try {
    const currentPath = currentPathWithQueryHash()

    // Call the backend logout API to clear server-side session
    try {
      await fetch(`${BACKEND_URI}/logout`, {
        method: "POST",
      })
    } catch {
      // Do nothing
    }

    if (!live()) return

    // Keep the identity record until the guarded navigation actually executes.
    // Removing it earlier would invalidate our own provider lease; a cancelled
    // delayed navigation must leave the current session untouched as well.
    const finishNavigation = (navigate: () => void) => {
      if (!live()) return
      try {
        sessionStorage.removeItem("easyauth_info")
        sessionStorage.removeItem("auth_return_to_v1")
        sessionStorage.removeItem("msal_login_redirect_started_v1")
      } catch {
        // Ignore unavailable session storage.
      }
      navigate()
    }

    // Redirect based on auth mode
    if (currentAuthMode === AuthMode.EASYAUTH && !isLocalDevHost()) {
      if (guardAuthLoop("easyauth", currentPath)) return

      const rootUrl = window.location.origin
      const returnUrl = encodeURIComponent(currentPath)

      // Direct login with prompt=login to force fresh authentication without account picker
      // This skips the .auth/logout endpoint which causes the "which account" prompt
      const loginUrl = `${rootUrl}/.auth/login/aad?prompt=login&post_login_redirect_uri=${returnUrl}`

      logger.info("Forcing fresh EasyAuth login without account picker prompt")

      // Short delay to see logs, then redirect
      await new Promise<void>((resolve) =>
        setTimeout(() => {
          finishNavigation(() => {
            window.location.href = loginUrl
          })
          resolve()
        }, 1000)
      )
    } else {
      if (guardAuthLoop("msal", currentPath)) return

      logger.info("Forcing logout (MSAL mode) - reloading page")
      await new Promise<void>((resolve) =>
        setTimeout(() => {
          finishNavigation(() => window.location.reload())
          resolve()
        }, 1000)
      )
    }
  } catch (error) {
    logger.error("Error during logout", error as Error)

    // Fallback: Force reload which will trigger re-auth
    setTimeout(() => {
      if (live()) window.location.reload()
    }, 1000)
  }
}

// Initialize authentication
export async function initializeAuth(): Promise<AuthMode> {
  // Initialize only once; auth provider selection should stay stable for the session.
  if (currentAuthMode !== AuthMode.UNKNOWN) {
    return currentAuthMode
  }

  // Determine whether EasyAuth is supported on this host (independent of login state).
  const supported = await probeEasyAuthSupport()
  if (supported) {
    logger.info("EasyAuth is supported on this host; using EasyAuth provider")
    currentAuthMode = AuthMode.EASYAUTH
    return currentAuthMode
  }

  logger.info("EasyAuth not supported; using MSAL provider")
  currentAuthMode = AuthMode.MSAL
  return currentAuthMode
}

/**
 * Handle an API error related to authentication.
 *
 * Thin delegate kept for compatibility with existing callers: all of the actual
 * work (single-flight refresh, epoch bookkeeping, deduplicated re-login) lives
 * in the coordinator. Note it no longer calls `checkEasyAuth()` — the EasyAuth
 * refresh path already verifies the new token against `/.auth/me` — so this
 * path never writes `currentAuthMode` (the #2312 boundary).
 *
 * Transports that can capture auth state at dispatch should call
 * `recoverFromUnauthorized()` with that snapshot instead; this entry point can
 * only snapshot at 401 time.
 */
export async function handleAuthError(response: Response): Promise<boolean> {
  if (response.status !== 401) return false

  logger.warn("Received 401 Unauthorized, attempting to refresh authentication")

  const outcome = await recoverFromUnauthorized(captureAuthSnapshot())
  return outcome.status === "refreshed" || outcome.status === "already-refreshed"
}

// Get the current authentication mode
export function getAuthMode(): AuthMode {
  return currentAuthMode
}

// Initialize auth and fetch auth setup
const authSetup = await fetchAuthSetup()
await initializeAuth()

export const useLogin = authSetup.useLogin

// Indicates whether the backend successfully delivered a usable MSAL config.
// "backend-unavailable" → the UI should render a "Backend starting" / error state
// and must NOT initialize MSAL with the (empty) fallback config.
export const authSetupStatus: AuthSetupStatus = authSetup.status

/**
 * Configuration object to be passed to MSAL instance on creation.
 * For a full list of MSAL.js configuration parameters, visit:
 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/configuration.md
 */
export const msalConfig = authSetup.msalConfig

/**
 * Scopes you add here will be prompted for user consent during sign-in.
 * By default, MSAL.js will add OIDC scopes (openid, profile, email) to any login request.
 * For more information about OIDC scopes, visit:
 * https://docs.microsoft.com/en-us/azure/active-directory/develop/v2-permissions-and-consent#openid-connect-scopes
 */
export const loginRequest = authSetup.loginRequest

const tokenRequest = authSetup.tokenRequest

// Build an absolute redirect URI using the current window's location and the relative redirect URI from auth setup
export const getRedirectUri = () => {
  return window.location.origin + authSetup.msalConfig.auth.redirectUri
}

// Get an access token for use with the API server.
// ID token received when logging in may not be used for this purpose because it has the incorrect audience
export const getToken = (
  client: IPublicClientApplication
): Promise<AuthenticationResult | undefined> => {
  // If using EasyAuth, we don't need to get a token from MSAL
  if (currentAuthMode === AuthMode.EASYAUTH) {
    return Promise.resolve(undefined)
  }

  return client
    .acquireTokenSilent({
      ...tokenRequest,
      redirectUri: getRedirectUri(),
    })
    .catch((error) => {
      logger.error("Error acquiring token silently", error as Error)
      return undefined
    })
}

// ── Bounded token renewal (#2904) ─────────────────────────────────────────────
// The coordinator owns concurrency and terminal state; everything below is the
// provider-specific half it calls into.

// The one navigation used when recovery is impossible. The coordinator fires it
// at most once per session generation, so concurrent 401s can no longer stack
// redirects and burn the `guardAuthLoop` burst budget.
configureAuthRecovery({ navigate: forceLogout })

/** Tenant/object identity of an MSAL account — never derived from a decoded access token. */
interface MsalSessionIdentity {
  homeAccountId: string
  tenantId: string
  localAccountId: string
}

function msalIdentityOf(account: AccountInfo | null | undefined): MsalSessionIdentity | null {
  if (!account?.homeAccountId || !account.tenantId || !account.localAccountId) return null
  return {
    homeAccountId: account.homeAccountId,
    tenantId: account.tenantId,
    localAccountId: account.localAccountId,
  }
}

function sameMsalIdentity(a: MsalSessionIdentity | null, b: MsalSessionIdentity | null): boolean {
  return (
    a !== null &&
    b !== null &&
    a.homeAccountId === b.homeAccountId &&
    a.tenantId === b.tenantId &&
    a.localAccountId === b.localAccountId
  )
}

function refreshEasyAuthCredential(isCurrent: () => boolean): Promise<AuthCredentialRefreshResult> {
  return refreshEasyAuthSession(isCurrent)
}

async function refreshMsalCredential(
  client: IPublicClientApplication,
  expected: MsalSessionIdentity,
  publication: {
    publishCredential: (token: string) => boolean
    isCurrent: () => boolean
    commitCredential: (publish: () => boolean) => boolean
  }
): Promise<AuthCredentialRefreshResult> {
  // Identity BEFORE the async hop: the account we were registered for must still
  // be the one MSAL holds, and it must still be the active one. We never fall
  // back to "some other signed-in account".
  const account = client.getAllAccounts().find((a) => sameMsalIdentity(msalIdentityOf(a), expected))
  if (!sameMsalIdentity(msalIdentityOf(account), expected)) {
    return { status: "failed", reason: "msal-account-missing" }
  }
  if (!sameMsalIdentity(msalIdentityOf(client.getActiveAccount()), expected)) {
    return { status: "failed", reason: "msal-active-account-changed" }
  }

  let result: AuthenticationResult
  try {
    // `forceRefresh` is the point: without it MSAL happily returns the same
    // cached (expired) access token that just produced the 401. Silent only —
    // recovery must never open an interactive prompt from inside a request.
    result = await client.acquireTokenSilent({
      ...tokenRequest,
      account,
      redirectUri: getRedirectUri(),
      forceRefresh: true,
    })
  } catch (error) {
    logger.warn("Silent token acquisition after 401 failed", error as Error)
    return { status: "failed", reason: "msal-silent-acquisition-failed" }
  }

  if (!result.accessToken) return { status: "failed", reason: "msal-no-access-token" }

  // Identity AFTER the async hop — on the result and on the instance. A token
  // for a different tenant/object must never be published as this session's.
  if (!sameMsalIdentity(msalIdentityOf(result.account), expected)) {
    return { status: "failed", reason: "msal-identity-mismatch" }
  }
  if (!sameMsalIdentity(msalIdentityOf(client.getActiveAccount()), expected)) {
    return { status: "failed", reason: "msal-active-account-changed" }
  }

  // Success requires the write-through to actually land: if nothing can hold the
  // new bearer, both transports would keep sending the dead one and the replay
  // would 401 again.
  if (!publication.isCurrent()) return { status: "failed", reason: "session-changed" }
  if (!publication.commitCredential(() => publication.publishCredential(result.accessToken))) {
    return { status: "failed", reason: "msal-token-write-through-failed" }
  }

  return { status: "refreshed" }
}

export interface AuthSessionRegistration {
  /** MSAL client for the signed-in session; null/ignored under EasyAuth. */
  msalInstance: IPublicClientApplication | null
  /** The account this session actually authenticated as. */
  account: AccountInfo | null
  /** Publishes a fresh bearer. Returns whether the write actually stuck. */
  publishCredential: (token: string) => boolean
  /** Reads the bearer currently managed for this session. */
  readCredential: () => string | undefined
}

/**
 * Registers the authenticated session with the refresh coordinator so 401s can
 * be recovered for this exact identity. Idempotent: re-registering the same
 * session (effect reruns, StrictMode double-mounts) swaps the closures without
 * disturbing an in-flight refresh; a different identity resets the coordinator.
 */
export function registerAuthSessionForCurrentMode(registration: AuthSessionRegistration): void {
  if (currentAuthMode === AuthMode.EASYAUTH) {
    const info = getStoredEasyAuthInfo()
    const identity = easyAuthIdentityOf(info)
    if (!identity) return
    registerAuthSession({
      id: `easyauth:${identity}`,
      isCurrent: () =>
        currentAuthMode === AuthMode.EASYAUTH &&
        easyAuthIdentityOf(getStoredEasyAuthInfo()) === identity,
      refresh: refreshEasyAuthCredential,
      // Cookie mode — there is no managed bearer, and the replay must not
      // resurrect a stale one.
      readCredential: () => undefined,
    })
    return
  }

  const client = registration.msalInstance
  const identity = msalIdentityOf(registration.account)
  if (!client || !identity) {
    logger.debug("Skipping auth session registration: no MSAL account identity available")
    return
  }

  registerAuthSession({
    id: `msal:${identity.tenantId}:${identity.localAccountId}:${identity.homeAccountId}`,
    isCurrent: () =>
      currentAuthMode === AuthMode.MSAL &&
      sameMsalIdentity(msalIdentityOf(client.getActiveAccount()), identity),
    refresh: (isCurrent, commitCredential) =>
      refreshMsalCredential(client, identity, {
        publishCredential: registration.publishCredential,
        isCurrent,
        commitCredential,
      }),
    readCredential: registration.readCredential,
  })
}

// Detect if an error is from a CORS-related auth redirect
export function isCorsRedirectError(error: unknown): boolean {
  return (
    (error instanceof TypeError && error.message.includes("Failed to fetch")) ||
    (error instanceof DOMException && error.name === "NetworkError")
  )
}

// Handle authentication redirect with a full page navigation
export function handleAuthRedirect(isCurrent: () => boolean = () => true): void {
  if (!isCurrent()) return
  logger.warn("Detected likely auth redirect - handling with full page navigation")

  if (guardAuthLoop("easyauth", currentPathWithQueryHash())) return

  // Refuse to redirect to /.auth/login/aad on a local dev host — the endpoint doesn't
  // exist there and the user would land on a 404. Surface a clear error instead.
  if (isLocalDevHost()) {
    logger.error("Refusing /.auth/login/aad redirect on local dev host (EasyAuth not available)")
    storeAuthError({
      reason: "EasyAuth not available in dev",
      message:
        "The frontend tried to redirect to /.auth/login/aad but EasyAuth isn't available on a local dev host. The backend probably restarted mid-session — reload the page to recover.",
      provider: "easyauth",
      returnTo: currentPathWithQueryHash(),
    })
    window.location.href = `${window.location.origin}/auth/error`
    return
  }

  // Build the redirect URL with current page as the return destination
  const currentUrl = encodeURIComponent(window.location.href)
  const redirectUrl = `${window.location.origin}/.auth/login/aad?post_login_redirect_uri=${currentUrl}`

  // Navigate with full page load (which doesn't have CORS restrictions)
  window.location.href = redirectUrl
}
